import type { QueryClient } from '@tanstack/react-query';
import type { ApiRequestObject } from '../flotiq-client/types/api-request';
import type {
  ContentObject,
  ContentTypeDefinition,
  Media,
  MediaHydrated,
  WithOptionalInternalProps,
} from '../flotiq-client/types/api-response';
import type { PluginPermission } from './types';

import { getToken } from '../../hooks/useToken';

import {
  deleteContentObject,
  getContentObject,
  getContentObjects,
  getContentObjectVersion,
  getContentObjectVersions,
  getContentType,
  listContentTypes,
  patchContentObject,
  postContentObject,
  putContentObject,
  putContentType,
} from '../flotiq-client';
import { getMediaUrl } from '../flotiq-client/api-helpers';
import {
  invalidateContentObjects,
  invalidateContentTypes,
} from '../queryClientHelpers';
import { RolePermissions } from '../rolePermissions';

class FlotiqScopedApiClient {
  #permissions;
  #emptyPermissions;
  #queryClient;

  constructor(permissions?: PluginPermission[], queryClient?: QueryClient) {
    this.#permissions = new RolePermissions(permissions);
    this.#emptyPermissions = !permissions || !permissions.length;
    this.#queryClient = queryClient;
  }

  #clearContentObjectsCache = (type: string, id?: string) => {
    if (!this.#queryClient) return;
    return invalidateContentObjects(this.#queryClient, type, id);
  };

  #clearContentTypesCache = (type: string) => {
    if (!this.#queryClient) return;
    invalidateContentTypes(this.#queryClient, type);
  };

  getObject(type: string, id: string) {
    if (this.#emptyPermissions || !this.#permissions.canCo(type))
      throw new Error(
        `Plugin does not have permission to read ${type} objects`,
      );

    return getContentObject(getToken(), undefined, {
      contentTypeName: type,
      id,
    });
  }

  getObjectVersions(type: string, id: string) {
    if (this.#emptyPermissions || !this.#permissions.canCo(type))
      throw new Error(
        `Plugin does not have permission to read ${type} objects`,
      );

    return getContentObjectVersions(getToken(), undefined, {
      contentTypeName: type,
      id,
    });
  }

  getObjectVersion(type: string, id: string, version: string) {
    if (this.#emptyPermissions || !this.#permissions.canCo(type))
      throw new Error(
        `Plugin does not have permission to read ${type} objects`,
      );

    return getContentObjectVersion(getToken(), undefined, {
      contentTypeName: type,
      id,
      versionNumber: version,
    });
  }

  listObjects(type: string, query: ApiRequestObject) {
    if (this.#emptyPermissions || !this.#permissions.canCo(type))
      throw new Error(
        `Plugin does not have permission to read ${type} objects`,
      );

    return getContentObjects(getToken(), undefined, {
      contentTypeName: type,
      ...query,
    });
  }

  postObject(type: string, object: WithOptionalInternalProps<ContentObject>) {
    if (
      this.#emptyPermissions ||
      !this.#permissions.canCo(type, RolePermissions.PERMISSIONS_TYPES.CREATE)
    )
      throw new Error(
        `Plugin does not have permission to create ${type} object`,
      );

    return postContentObject(getToken(), undefined, {
      contentTypeName: type,
      ...object,
    }).then((response) => {
      this.#clearContentObjectsCache(type);
      return response;
    });
  }

  putObject(
    type: string,
    id: string,
    object: WithOptionalInternalProps<ContentObject>,
  ) {
    if (
      this.#emptyPermissions ||
      !this.#permissions.canCo(type, RolePermissions.PERMISSIONS_TYPES.UPDATE)
    )
      throw new Error(
        `Plugin does not have permission to update ${type} objects`,
      );

    return putContentObject(getToken(), undefined, {
      id,
      contentTypeName: type,
      ...object,
    }).then((response) => {
      this.#clearContentObjectsCache(type, id);
      return response;
    });
  }

  patchObject(type: string, id: string, object: Partial<ContentObject>) {
    if (
      this.#emptyPermissions ||
      !this.#permissions.canCo(type, RolePermissions.PERMISSIONS_TYPES.UPDATE)
    )
      throw new Error(
        `Plugin does not have permission to update ${type} objects`,
      );

    return patchContentObject(getToken(), undefined, {
      id,
      contentTypeName: type,
      ...object,
    }).then((response) => {
      this.#clearContentObjectsCache(type, id);
      return response;
    });
  }

  deleteObject(type: string, id: string) {
    if (
      this.#emptyPermissions ||
      !this.#permissions.canCo(type, RolePermissions.PERMISSIONS_TYPES.DELETE)
    )
      throw new Error(
        `Plugin does not have permission to delete ${type} objects`,
      );

    return deleteContentObject(getToken(), undefined, {
      id,
      contentTypeName: type,
    }).then((response) => {
      this.#clearContentObjectsCache(type, id);
      return response;
    });
  }

  getContentTypes(params: ApiRequestObject) {
    if (this.#emptyPermissions || !this.#permissions.canCtd('*'))
      throw new Error(
        'Plugin does not have permission to read all definition types',
      );

    return listContentTypes(getToken(), undefined, params);
  }

  getContentType(type: string) {
    if (this.#emptyPermissions || !this.#permissions.canCtd(type))
      throw new Error(
        `Plugin does not have permission to read ${type} definition type`,
      );

    return getContentType(getToken(), undefined, { contentTypeName: type });
  }

  putContentType(
    type: string,
    object: WithOptionalInternalProps<ContentTypeDefinition>,
  ) {
    if (
      this.#emptyPermissions ||
      !this.#permissions.canCtd(type, RolePermissions.PERMISSIONS_TYPES.UPDATE)
    )
      throw new Error(
        `Plugin does not have permission to update ${type} definition type`,
      );

    return putContentType(getToken(), undefined, {
      contentTypeName: type,
      ...object,
    }).then((response) => {
      this.#clearContentTypesCache(type);
      return response;
    });
  }

  getMediaUrl(mediaData: Media | MediaHydrated, height = 0, width = 0) {
    if (this.#emptyPermissions || !this.#permissions.canCo('_media'))
      throw new Error(`Plugin does not have permission to read media`);

    return getMediaUrl(mediaData, width, height);
  }
}

export { FlotiqScopedApiClient };
